import {
    CheckmarkCircleOutline,
    PhonePortraitOutline,
    TimeOutline,
    WalletOutline,
  } from "@raresail/react-ionicons";
  import { Button } from "../../components/common/Button";
  import {
    IconBox,
    ListItemWrapper,
    StatusTag,
  } from "../../components/elements/Elements";
  import { API_DEVICE_ORDERS } from "../../constants/apis";
  import { DEVICE_ORDERS, SELECT } from "../../constants/constants";
  import { searchFilterField } from "../../constants/options";
  import {
    addSelectOption,
    currencyFormat,
    formatDate,
    formatDateTime,
  } from "../../utils/helpers";
  import { openBottomSheet, openModalForm } from "../../utils/modalUtils";
  
  export const useDeviceOrdersList = () => {
    const filters = [
      searchFilterField,
      {
        field: "delivery_status",
        type: SELECT,
        options: ["pending", "shipped", "delivered", "confirmed"].map((v) =>
          addSelectOption(v)
        ),
      },
    ];
  
    const viewData = (data: any) => {
      const {
        device,
        loan_id,
        loan_status,
        selected_plan_months,
        deposit_amount,
        deposit_paid,
        deposit_paid_at,
        deposit_refunded,
        deposit_refunded_at,
        deposit_state,
        total_financed_amount,
        monthly_installment,
        delivery_status,
        delivery_confirmed_at,
        created_at,
      } = data || {};

      // Backend lifecycle: unpaid → in_escrow → (released | refunded)
      const depositStateLabel: Record<string, string> = {
        unpaid: "Not paid",
        in_escrow: "Held in escrow",
        released: "Applied to purchase",
        refunded: "Refunded to wallet",
      };

      return {
        device: device?.name,
        brand: `${device?.brand} ${device?.model || ""}`.trim(),
        plan: `${selected_plan_months} months`,
        deposit: currencyFormat(deposit_amount),
        deposit_status: depositStateLabel[deposit_state] || deposit_state || "—",
        financed_amount: currencyFormat(total_financed_amount),
        monthly_installment: currencyFormat(monthly_installment),
        loan_id: loan_id,
        loan_status: loan_status ? <StatusTag status={loan_status} /> : "N/A",
        delivery_status: <StatusTag status={delivery_status} />,
        ...(deposit_paid_at
          ? { deposit_paid_at: formatDateTime(deposit_paid_at) }
          : {}),
        ...(deposit_refunded && deposit_refunded_at
          ? { deposit_refunded_at: formatDateTime(deposit_refunded_at) }
          : {}),
        ...(delivery_confirmed_at
          ? { delivery_confirmed_at: formatDateTime(delivery_confirmed_at) }
          : {}),
        created_at: formatDateTime(created_at),
      };
    };
  
    const ListItem = ({ data }: { data: any }) => {
      const {
        device,
        deposit_paid,
        deposit_refunded,
        deposit_state,
        delivery_status,
        monthly_installment,
        selected_plan_months,
        created_at,
        loan_status,
      } = data || {};

      const loanApproved = loan_status === "approved";
  
      return (
        <ListItemWrapper
          className="flexNullCenter gap15"
          onClick={() =>
            openBottomSheet({
              component: "details",
              title: "Device Order",
              source: DEVICE_ORDERS,
              id: data?.id,
              data,
              state: data,
              footer: OrderFooter,
            })
          }
        >
          <IconBox icon={PhonePortraitOutline} size={20} />
  
          {/* MID */}
          <div className="flexGrow">
            <div className="font13 semiBold textDark capitalize mb3">
              {device?.name}
            </div>
            <div className="font12 grey capitalize">
              {device?.brand} · {selected_plan_months} months
            </div>
            <div className="font11 grey flexNullCenter gap5 mt3">
              <TimeOutline fontSize={12} />
              {formatDate(created_at)}
            </div>
          </div>
  
          {/* END */}
          <div className="textEnd">
            <div className="font13 semiBold textDark mb5">
              {currencyFormat(monthly_installment)}/mo
            </div>
            <StatusTag status={delivery_status} />
            {deposit_state === "refunded" && (
              <div className="font10 mt3" style={{ color: "#b91c1c" }}>
                Deposit refunded
              </div>
            )}
            {deposit_state === "in_escrow" && (
              <div className="font10 mt3" style={{ color: "#0369a1" }}>
                Deposit in escrow
              </div>
            )}
            {/* Legacy orders created before deposits moved up front */}
            {deposit_state === "unpaid" && loanApproved && (
              <div className="font10 mt3" style={{ color: "#b45309" }}>
                Deposit pending
              </div>
            )}
            {deposit_state === "unpaid" && !loanApproved && (
              <div className="font10 red mt3">Awaiting approval</div>
            )}
          </div>
        </ListItemWrapper>
      );
    };
  
    return {
      ListItem,
      title: "My BNPL Orders",
      subtitle: "Track your BNPL orders",
      api: API_DEVICE_ORDERS,
      filters,
      viewData,
    };
  };
  
  export const OrderFooter = ({
    data,
    details,
    state,
    dismiss,
  }: {
    data?: any;
    details?: any;
    state?: any;
    dismiss?: any;
  }) => {
    const raw = details || state;
    const { deposit_paid, delivery_status, loan_status } = raw || {};
  
    const loanApproved = loan_status === "approved";
  
    if (!deposit_paid && loanApproved) {
      return (
        <div className="flexColumn gap10 p15">
          <Button
            className="x100"
            icon={<WalletOutline style={{ marginRight: 6 }} />}
            onClick={() => {
              dismiss?.();
              openModalForm({
                source: "pay_deposit",
                state: raw,
              });
            }}
          >
            Pay Deposit
          </Button>
        </div>
      );
    }
  
    if (
      deposit_paid &&
      delivery_status === "delivered" &&
      delivery_status !== "confirmed"
    ) {
      return (
        <div className="flexColumn gap10 p15">
          <Button
            className="x100"
            icon={<CheckmarkCircleOutline style={{ marginRight: 6 }} />}
            onClick={() => {
              dismiss?.();
              openModalForm({
                source: "confirm_delivery",
                state: raw,
              });
            }}
          >
            Confirm Delivery
          </Button>
        </div>
      );
    }
  
    return null;
  };